<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import WhatsAppTemplateBuilder from './components/WhatsAppTemplateBuilder.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const T = 'WHATSAPP_TEMPLATES.MANAGEMENT';

const state = reactive({
  templates: [],
  isLoading: false,
  isSyncing: false,
  searchQuery: '',
  categoryFilter: '',
  statusFilter: '',
});

const showBuilder = ref(false);
const editingTemplate = ref(null);
// GK-HUB: usar woot-delete-modal em vez de window.confirm nativo
const showDeleteModal = ref(false);
const templateToDelete = ref(null);
const expandedCards = reactive({});

const STATUS_STYLES = {
  APPROVED: {
    class: 'bg-[#10b98114] text-[#10b981] border border-[#10b98130]',
    icon: '\u2713',
  },
  PENDING: {
    class: 'bg-[#f59e0b14] text-[#f59e0b] border border-[#f59e0b30]',
    icon: '\u25F7',
  },
  REJECTED: {
    class: 'bg-[#ef444414] text-[#ef4444] border border-[#ef444430]',
    icon: '\u2717',
  },
  PAUSED: {
    class: 'bg-[#f59e0b14] text-[#f59e0b] border border-[#f59e0b30]',
    icon: '\u23F8',
  },
  DISABLED: {
    class: 'bg-n-slate-3 text-n-slate-9 border border-transparent',
    icon: '\u26D4',
  },
};

const QUALITY_STYLES = {
  GREEN: {
    class:
      'bg-n-teal-11 shadow-[0_0_6px_rgba(45,212,168,0.4)] animate-[wa-pulseGlow_3s_infinite]',
    label: 'GREEN',
  },
  YELLOW: {
    class: 'bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.4)]',
    label: 'YELLOW',
  },
  RED: {
    class: 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.4)]',
    label: 'RED',
  },
};

const CATEGORY_STYLES = {
  MARKETING: 'bg-[#6366f114] text-[#818cf8] border border-[#6366f130]',
  UTILITY: 'bg-[#10b98114] text-[#10b981] border border-[#10b98130]',
};

const filteredTemplates = computed(() => {
  let result = state.templates;
  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    result = result.filter(tpl => tpl.name.toLowerCase().includes(q));
  }
  if (state.categoryFilter) {
    result = result.filter(
      tpl => tpl.category === state.categoryFilter.toUpperCase()
    );
  }
  if (state.statusFilter) {
    result = result.filter(
      tpl => tpl.status === state.statusFilter.toUpperCase()
    );
  }
  return result;
});

// Pagination
const currentPage = ref(1);
const PER_PAGE = 20;

const paginatedTemplates = computed(() => {
  const start = (currentPage.value - 1) * PER_PAGE;
  return filteredTemplates.value.slice(start, start + PER_PAGE);
});

const totalPages = computed(() =>
  Math.ceil(filteredTemplates.value.length / PER_PAGE)
);

watch(
  [
    () => state.searchQuery,
    () => state.categoryFilter,
    () => state.statusFilter,
  ],
  () => {
    currentPage.value = 1;
  }
);

const stats = computed(() => {
  const all = state.templates;
  return {
    total: all.length,
    approved: all.filter(tpl => tpl.status === 'APPROVED').length,
    pending: all.filter(tpl => tpl.status === 'PENDING').length,
    rejected: all.filter(tpl => tpl.status === 'REJECTED').length,
    paused: all.filter(tpl => tpl.status === 'PAUSED').length,
    marketing: all.filter(tpl => tpl.category === 'MARKETING').length,
    utility: all.filter(tpl => tpl.category === 'UTILITY').length,
  };
});

const fetchTemplates = async () => {
  state.isLoading = true;
  try {
    const result = await store.dispatch('inboxes/listTemplates', {
      inboxId: props.inbox.id,
      filters: {},
    });
    state.templates = result.data || [];
  } catch (e) {
    useAlert(t(`${T}.SYNC_ERROR`));
  } finally {
    state.isLoading = false;
  }
};

const syncTemplates = async () => {
  state.isSyncing = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    await fetchTemplates();
    useAlert(t(`${T}.SYNC_SUCCESS`));
  } catch (e) {
    useAlert(t(`${T}.SYNC_ERROR`));
  } finally {
    state.isSyncing = false;
  }
};

// GK-HUB: abre modal padrão woot-delete-modal em vez de window.confirm
const deleteTemplate = template => {
  templateToDelete.value = template;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  templateToDelete.value = null;
};

const confirmDeleteTemplate = async () => {
  const template = templateToDelete.value;
  if (!template) return;
  showDeleteModal.value = false;
  try {
    await store.dispatch('inboxes/deleteTemplate', {
      inboxId: props.inbox.id,
      templateName: template.name,
      templateId: template.id,
    });
    useAlert(t(`${T}.DELETE_SUCCESS`));
    await fetchTemplates();
  } catch (e) {
    useAlert(t(`${T}.DELETE_ERROR`));
  } finally {
    templateToDelete.value = null;
  }
};

const openBuilder = (template = null) => {
  editingTemplate.value = template;
  showBuilder.value = true;
};

const closeBuilder = () => {
  showBuilder.value = false;
  editingTemplate.value = null;
  fetchTemplates();
};

const getBodyText = template => {
  const body = template.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

const getHeaderFormat = template => {
  const header = template.components?.find(c => c.type === 'HEADER');
  return header?.format || null;
};

const getButtons = template => {
  const buttons = template.components?.find(c => c.type === 'BUTTONS');
  return buttons?.buttons || [];
};

const getQualityScore = template => {
  const score = template.quality_score?.score;
  if (!score || score === 'UNKNOWN') return null;
  return score;
};

const getStatusStyle = status =>
  STATUS_STYLES[status] || STATUS_STYLES.DISABLED;
const getQualityStyle = score => QUALITY_STYLES[score] || null;
const getCategoryStyle = category =>
  CATEGORY_STYLES[category] || CATEGORY_STYLES.UTILITY;

const toggleExpand = key => {
  expandedCards[key] = !expandedCards[key];
};

const statusLabel = status => t(`${T}.STATUS.${status}`) || status;
const qualityLabel = score => {
  const key = `${T}.QUALITY.${score}`;
  const val = t(key);
  return val !== key ? val : score;
};

const escapeHtml = str => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

const highlightVars = text => {
  return escapeHtml(text).replace(
    /\{\{(\d+)\}\}/g,
    '<span class="wa-tpl-var">{​{$1}​}</span>'
  );
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <!-- GK-HUB: padding vertical maior (respiração do topo, alinha com outras abas) -->
  <div class="py-6">
    <!-- Builder view -->
    <WhatsAppTemplateBuilder
      v-if="showBuilder"
      :inbox="inbox"
      :template="editingTemplate"
      @close="closeBuilder"
    />

    <!-- List view -->
    <div v-else>
      <!-- Header -->
      <div class="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12 tracking-tight">
            {{ t(`${T}.TITLE`) }}
          </h2>
          <p class="text-[13px] text-n-slate-10 mt-0.5">
            {{ t(`${T}.SUBTITLE`) }}
          </p>
        </div>
        <div class="flex gap-3 items-center">
          <!-- GK-HUB: botão secundário com border visível (antes parecia link) -->
          <button
            class="wa-sync-btn"
            :disabled="state.isSyncing"
            @click="syncTemplates"
          >
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span :class="{ 'wa-spin': state.isSyncing }">&#x21BB;</span>
            {{ t(`${T}.SYNC_BUTTON`) }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-br from-n-blue-9 to-n-blue-11 shadow-md hover:shadow-lg hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none border-none cursor-pointer"
            @click="openBuilder(null)"
          >
            + {{ t(`${T}.NEW_TEMPLATE`) }}
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div v-if="state.templates.length > 0" class="flex gap-4 mb-5 flex-wrap">
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-n-solid-2 border border-n-weak text-n-slate-10"
        >
          {{ t(`${T}.STATS.TOTAL`) }}
          <strong class="font-bold text-n-slate-12">{{ stats.total }}</strong>
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-n-solid-2 border border-n-weak text-n-slate-10"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-n-teal-11" />
          {{ t(`${T}.STATS.APPROVED`) }}
          <strong class="font-bold text-n-slate-12">{{
            stats.approved
          }}</strong>
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-n-solid-2 border border-n-weak text-n-slate-10"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
          {{ t(`${T}.STATS.PENDING`) }}
          <strong class="font-bold text-n-slate-12">{{ stats.pending }}</strong>
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-n-solid-2 border border-n-weak text-n-slate-10"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-n-ruby-11" />
          {{ t(`${T}.STATS.REJECTED`) }}
          <strong class="font-bold text-n-slate-12">{{
            stats.rejected
          }}</strong>
        </span>
        <span
          v-if="stats.paused > 0"
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-n-solid-2 border border-n-weak text-n-slate-10"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
          {{ t(`${T}.STATS.PAUSED`) }}
          <strong class="font-bold text-n-slate-12">{{ stats.paused }}</strong>
        </span>
        <!-- Category stats -->
        <span class="w-px h-5 bg-n-weak mx-1" />
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-[#6366f114] border border-[#6366f130] text-[#818cf8]"
        >
          {{ t(`${T}.FILTER_MARKETING`) }}
          <strong class="font-bold">{{ stats.marketing }}</strong>
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] bg-[#10b98114] border border-[#10b98130] text-[#10b981]"
        >
          {{ t(`${T}.FILTER_UTILITY`) }}
          <strong class="font-bold">{{ stats.utility }}</strong>
        </span>
      </div>

      <!-- Filters — GK-HUB: padrão ISP com alturas alinhadas -->
      <div v-if="state.templates.length > 0" class="isp-filter-row mb-5">
        <input
          v-model="state.searchQuery"
          type="text"
          :placeholder="t(`${T}.SEARCH_PLACEHOLDER`)"
          class="isp-input isp-search"
        />
        <div class="isp-select-wrap">
          <select v-model="state.categoryFilter" class="isp-select">
            <option value="">{{ t(`${T}.FILTER_ALL_CATEGORIES`) }}</option>
            <option value="MARKETING">{{ t(`${T}.FILTER_MARKETING`) }}</option>
            <option value="UTILITY">{{ t(`${T}.FILTER_UTILITY`) }}</option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
        <div class="isp-select-wrap">
          <select v-model="state.statusFilter" class="isp-select">
            <option value="">{{ t(`${T}.FILTER_ALL_STATUS`) }}</option>
            <option value="APPROVED">{{ t(`${T}.STATUS.APPROVED`) }}</option>
            <option value="PENDING">{{ t(`${T}.STATUS.PENDING`) }}</option>
            <option value="REJECTED">{{ t(`${T}.STATUS.REJECTED`) }}</option>
            <option value="PAUSED">{{ t(`${T}.STATUS.PAUSED`) }}</option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
      </div>

      <!-- Loading -->
      <div
        v-if="state.isLoading"
        class="flex justify-center py-10 text-2xl text-n-slate-9"
      >
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="wa-spin">&#x21BB;</span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="state.templates.length === 0 && !state.isLoading"
        class="flex flex-col items-center justify-center py-20 px-10 text-center"
      >
        <div
          class="w-[120px] h-[120px] rounded-full flex items-center justify-center mb-7 text-5xl leading-none opacity-20 bg-n-solid-2 border-2 border-dashed border-n-weak"
        >
          &#x1F4AC;
        </div>
        <h3 class="text-lg font-bold text-n-slate-12 mb-2">
          {{ t(`${T}.EMPTY_TITLE`) }}
        </h3>
        <p class="text-sm text-n-slate-10 mb-7 max-w-[360px] leading-relaxed">
          {{ t(`${T}.EMPTY_SUBTITLE`) }}
        </p>
        <button
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-br from-n-blue-9 to-n-blue-11 shadow-md hover:shadow-lg hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none border-none cursor-pointer"
          @click="openBuilder(null)"
        >
          + {{ t(`${T}.CREATE_FIRST`) }}
        </button>
      </div>

      <!-- No results from filter -->
      <div
        v-else-if="filteredTemplates.length === 0"
        class="flex flex-col items-center justify-center py-8 px-5"
      >
        <p class="text-sm text-n-slate-10">
          {{ t(`${T}.EMPTY_TITLE`) }}
        </p>
      </div>

      <!-- Template cards -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div
          v-for="tpl in paginatedTemplates"
          :key="tpl.id || tpl.name"
          class="rounded-[10px] p-4 transition-all animate-[wa-slideUp_0.4s_ease_backwards] cursor-default bg-n-solid-2 border border-n-weak hover:border-n-blue-9 hover:shadow-[0_0_20px_rgba(46,134,193,0.15)] flex flex-col"
        >
          <div class="flex-1">
            <div class="mb-2">
              <span
                class="text-[14px] font-semibold text-n-slate-12 tracking-tight break-all"
              >
                {{ tpl.name }}
              </span>
              <div class="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                  :class="getCategoryStyle(tpl.category)"
                >
                  {{ tpl.category }}
                </span>
                <span class="text-n-slate-8 text-[8px]">&#x2022;</span>
                <span class="text-xs font-medium text-n-slate-9">
                  {{ tpl.language }}
                </span>
                <span class="text-n-slate-8 text-[8px]">&#x2022;</span>
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                  :class="getStatusStyle(tpl.status).class"
                >
                  {{ getStatusStyle(tpl.status).icon }}
                  {{ statusLabel(tpl.status) }}
                </span>
                <template v-if="getQualityScore(tpl)">
                  <span class="text-n-slate-8 text-[8px]">&#x2022;</span>
                  <span
                    class="inline-block w-2 h-2 rounded-full align-middle"
                    :class="getQualityStyle(getQualityScore(tpl))?.class"
                  />
                  <span class="text-[11px] text-n-slate-9">
                    {{ qualityLabel(getQualityScore(tpl)) }}
                  </span>
                </template>
              </div>
            </div>

            <!-- Header indicator -->
            <div
              v-if="getHeaderFormat(tpl)"
              class="text-xs text-n-slate-9 mb-1.5"
            >
              {{ t(`${T}.HEADER_${getHeaderFormat(tpl)}`) }}
            </div>

            <!-- Body preview (truncated, click to expand) -->
            <div
              v-if="getBodyText(tpl)"
              class="text-[12px] leading-relaxed px-3 py-2 rounded-lg mb-2 bg-n-solid-3 text-n-slate-11 border-l-[3px] border-n-weak cursor-pointer transition-all"
              :class="expandedCards[tpl.id || tpl.name] ? '' : 'line-clamp-3'"
              @click="toggleExpand(tpl.id || tpl.name)"
              v-html="highlightVars(getBodyText(tpl))"
            />

            <!-- Buttons preview -->
            <div
              v-if="getButtons(tpl).length > 0"
              class="flex gap-1.5 flex-wrap"
            >
              <span
                v-for="(btn, idx) in getButtons(tpl)"
                :key="idx"
                class="px-3 py-1 border border-n-weak rounded-full text-xs text-n-blue-11 bg-n-blue-3/30"
              >
                {{ btn.text }}
              </span>
            </div>

            <!-- Rejected reason -->
            <div
              v-if="
                tpl.status === 'REJECTED' &&
                tpl.rejected_reason &&
                tpl.rejected_reason !== 'NONE'
              "
              class="flex items-center gap-2 mt-2 px-3 py-2 bg-n-ruby-3 border border-n-ruby-6 rounded-md text-xs text-n-ruby-11"
            >
              <!-- eslint-disable vue/no-bare-strings-in-template -->&#x26A0;
              {{ t(`${T}.REJECTED_REASON`) }}: {{ tpl.rejected_reason
              }}<!-- eslint-enable vue/no-bare-strings-in-template -->
            </div>
          </div>
          <!-- Card actions (bottom) -->
          <div class="flex gap-1 mt-auto pt-2 border-t border-n-weak/50">
            <!-- GK-HUB: botões com border (antes pareciam links) -->
            <button
              v-if="tpl.status === 'APPROVED' || tpl.status === 'REJECTED'"
              class="wa-card-btn wa-card-btn--edit"
              @click="openBuilder(tpl)"
            >
              <span class="i-lucide-pencil w-3.5 h-3.5" />
              {{ t(`${T}.EDIT`) }}
            </button>
            <button
              class="wa-card-btn wa-card-btn--delete"
              @click="deleteTemplate(tpl)"
            >
              <span class="i-lucide-trash-2 w-3.5 h-3.5" />
              {{ t(`${T}.DELETE`) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between mt-5 pt-4 border-t border-n-weak text-[12px] text-n-slate-10"
      >
        <button
          class="px-3 py-1.5 rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-transparent cursor-pointer"
          :disabled="currentPage <= 1"
          @click="currentPage--"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template -->&#x2190;
          {{ t(`${T}.PREVIOUS`)
          }}<!-- eslint-enable vue/no-bare-strings-in-template -->
        </button>
        <span class="text-n-slate-9">
          {{
            t(`${T}.PAGINATION_INFO`, {
              from: (currentPage - 1) * PER_PAGE + 1,
              to: Math.min(currentPage * PER_PAGE, filteredTemplates.length),
              total: filteredTemplates.length,
            })
          }}
        </span>
        <button
          class="px-3 py-1.5 rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-transparent cursor-pointer"
          :disabled="currentPage >= totalPages"
          @click="currentPage++"
        >
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          {{ t(`${T}.NEXT`) }} &#x2192;
        </button>
      </div>
    </div>

    <!-- GK-HUB: Modal padrão de exclusão (substitui window.confirm nativo) -->
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeleteTemplate"
      :title="t(`${T}.DELETE_CONFIRM_TITLE`)"
      :message="t(`${T}.DELETE_CONFIRM_MESSAGE`)"
      :message-value="
        templateToDelete ? `\u201C${templateToDelete.name}\u201D?` : ''
      "
      :confirm-text="t(`${T}.DELETE_CONFIRM_YES`)"
      :reject-text="t(`${T}.DELETE_CONFIRM_NO`)"
    />
  </div>
</template>

<style scoped>
/* ── Select fix: force remove native arrow (Chatwoot global CSS overrides appearance) ── */
.wa-select {
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background-image: none !important;
}

/* ── Animations (kept — can't be expressed as Tailwind utilities) ── */
@keyframes wa-slideUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes wa-pulseGlow {
  0%,
  100% {
    box-shadow: 0 0 6px rgba(45, 212, 168, 0.3);
  }
  50% {
    box-shadow: 0 0 12px rgba(45, 212, 168, 0.6);
  }
}
.wa-spin {
  animation: wa-spin 1s linear infinite;
  display: inline-block;
}
@keyframes wa-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Template variable highlight (injected via v-html) ──────────── */
:deep(.wa-tpl-var) {
  color: var(--n-blue-11, #5dade2);
  font-weight: 600;
  background: var(--n-blue-3, rgba(93, 173, 226, 0.1));
  padding: 1px 4px;
  border-radius: 3px;
}

/* GK-HUB: Linha dos filtros — altura unificada, sem wrap indesejado */
.isp-filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.isp-search {
  flex: 1 1 0%;
  min-width: 0;
  /* flex-basis 0 força o input a NÃO considerar conteúdo na hora de dividir espaço;
     ele simplesmente ocupa o que sobrou após os selects terem seus tamanhos fixos */
}

/* GK-HUB: Padrão ISP — input + select com MESMA altura (34px).
   Uso !important porque CSS global do Chatwoot força height maior em input[type]. */
.isp-input {
  box-sizing: border-box !important;
  height: 34px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  font-size: 13px !important;
  font-family: inherit;
  line-height: 1.5;
  border-radius: 8px !important;
  outline: none !important;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6 !important;
  transition: border-color 150ms ease;
}
.isp-input::placeholder {
  color: #9ca3af;
}
.isp-input:hover {
  border-color: #cbd0d6;
}
.isp-input:focus {
  border-color: #2e86c1;
}

/* GK-HUB: Select wrapper (arrow custom, padrão dos relatórios ISP) */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  box-sizing: border-box;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  height: 34px;
  min-width: 180px;
  margin: 0;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  padding: 0 30px 0 12px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* GK-HUB: Botão Sincronizar — secundário com border visível */
.wa-sync-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  background: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
  cursor: pointer;
  transition: all 150ms ease;
}
.wa-sync-btn:hover:not(:disabled) {
  background: #eef0f3;
  border-color: #cbd0d6;
  color: #1e2733;
}
.wa-sync-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* GK-HUB: Botões Editar/Excluir no card — antes pareciam links */
.wa-card-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid;
  background: transparent;
  cursor: pointer;
  transition: all 150ms ease;
}
.wa-card-btn--edit {
  color: var(--n-slate-11, #5a6d82);
  border-color: var(--n-weak, #e0e2e6);
}
.wa-card-btn--edit:hover {
  background: var(--n-alpha-1, rgba(0, 0, 0, 0.03));
  border-color: #cbd0d6;
  color: var(--n-slate-12, #1e2733);
}
.wa-card-btn--delete {
  color: var(--n-ruby-11, #d93f5e);
  border-color: rgba(217, 63, 94, 0.3);
}
.wa-card-btn--delete:hover {
  background: rgba(217, 63, 94, 0.08);
  border-color: var(--n-ruby-11, #d93f5e);
}
</style>

<!-- GK-HUB: Dark mode — !important necessário para vencer os !important do light -->
<style>
body.dark .isp-input,
body.dark .isp-select {
  background-color: transparent !important;
  color: rgba(255, 255, 255, 0.72) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
body.dark .isp-input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}
body.dark .isp-input:hover,
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
}
body.dark .isp-input:focus,
body.dark .isp-select:focus {
  border-color: rgba(93, 173, 226, 0.6) !important;
}
body.dark .isp-select-arrow {
  color: #64748b;
}
body.dark .wa-card-btn--edit {
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.12);
}
body.dark .wa-card-btn--edit:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
}
body.dark .wa-sync-btn {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.2);
}
body.dark .wa-sync-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.35);
  color: rgba(255, 255, 255, 1);
}
</style>
